import apiClient from '../api/apiClient';
import { SamlAuthResponse } from '../../types/saml';

/**
 * SAML Service
 * Handles SAML authentication response generation and redirection
 */
class SamlService {
  private static readonly API_TIMEOUT = 10000; // 10 seconds

  /**
   * Validates required parameters for SAML requests
   */
  private static validateParameters(sessionId: string, username: string): void {
    if (!sessionId || !username) {
      throw new Error('Session ID and username are required');
    }
  }

  /**
   * Sends authentication response to SAML service and redirects user
   * This method receives HTML content from backend and renders it to trigger SAML redirect
   * 
   * @param sessionId - The session ID for authentication
   * @param username - The authenticated username
   * @throws Error if validation fails or SAML service returns an error
   */
  static async sendAuthResponseAndRedirect(
    sessionId: string,
    username: string
  ): Promise<void> {
    try {
      this.validateParameters(sessionId, username);

      const requestBody = new URLSearchParams({
        sessionId,
        username
      });

      const response = await apiClient.post(
        'auth/saml/authResponse',
        requestBody.toString(),
        {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded'
          },
          timeout: this.API_TIMEOUT,
          responseType: 'text'
        }
      );

      const htmlContent = response.data;

      if (!htmlContent || htmlContent.trim().length === 0) {
        throw new Error('Received empty response from SAML service');
      }

      document.open();
      document.write(htmlContent);
      document.close();
    } catch (error: any) {
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        throw new Error('Request timeout - SAML service did not respond in time');
      }
      throw error;
    }
  }

  /**
   * Sends authentication response to SAML service and returns result
   * This method is used when you need to handle the response programmatically
   * 
   * @param sessionId - The session ID for authentication
   * @param username - The authenticated username
   * @returns SAML authentication response with success status
   */
  static async sendAuthResponse(
    sessionId: string,
    username: string
  ): Promise<SamlAuthResponse> {
    try {
      this.validateParameters(sessionId, username);

      const requestBody = new URLSearchParams({
        sessionId,
        username
      });

      const response = await apiClient.post<SamlAuthResponse>(
        '/saml/authResponse',
        requestBody.toString(),
        {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded'
          },
          timeout: this.API_TIMEOUT
        }
      );

      return {
        success: true,
        message: response.data.message || 'SAML response generated successfully'
      };
    } catch (error: any) {
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        return {
          success: false,
          error: 'Request timeout - SAML service did not respond in time'
        };
      }

      if (error.response?.data) {
        return {
          success: false,
          error: error.response.data.error || error.response.data.message || 'SAML response generation failed'
        };
      }

      return {
        success: false,
        error: error.message || 'Failed to send authentication response'
      };
    }
  }
}

export default SamlService;
